import { useEffect } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useToast } from "@/shared/hooks/use-toast";
import { creatorFinancialApi } from "../api/creatorFinancial.api";
import type {
  CreatorBankAccount,
  UpdateCreatorBankAccountPayload,
  CreateCreatorBankAccountPayload,
  CreatorReceivableSnapshot,
  GetCreatorReceivableSnapshotsParams,
  CreatorPayment,
  GetCreatorPaymentsParams,
  PaginatedCreatorReceivableSnapshotsResponse,
  PaginatedCreatorPaymentsResponse,
} from "../types";
import { creatorFinancialKeys } from "./creatorFinancial.keys";

export function useCreatorFinancialBankAccounts(
  options?: UseQueryOptions<CreatorBankAccount[], Error>
) {
  const { toast } = useToast();
  const query = useQuery({
    queryKey: creatorFinancialKeys.bankAccounts(),
    queryFn: ({ signal }) => creatorFinancialApi.bankAccounts.list(signal),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar contas bancárias",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    bankAccounts: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useUpdateCreatorFinancialBankAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: [...creatorFinancialKeys.bankAccounts(), "update"],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateCreatorBankAccountPayload;
    }) => creatorFinancialApi.bankAccounts.update(id, payload),
    onSuccess: () => {
      toast({ title: "Conta atualizada com sucesso", variant: "success" });
      queryClient.invalidateQueries({ queryKey: creatorFinancialKeys.bankAccounts() });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao atualizar conta",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    updateBankAccount: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useCreateCreatorFinancialBankAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: [...creatorFinancialKeys.bankAccounts(), "create"],
    mutationFn: (payload: CreateCreatorBankAccountPayload) =>
      creatorFinancialApi.bankAccounts.create(payload),
    onSuccess: () => {
      toast({ title: "Conta cadastrada com sucesso", variant: "success" });
      queryClient.invalidateQueries({ queryKey: creatorFinancialKeys.bankAccounts() });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao cadastrar conta",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    createBankAccount: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useCreatorReceivableSnapshots(
  params?: GetCreatorReceivableSnapshotsParams,
  options?: UseQueryOptions<PaginatedCreatorReceivableSnapshotsResponse, Error>
) {
  const { toast } = useToast();
  const query = useQuery({
    queryKey: creatorFinancialKeys.receivableSnapshots(params),
    queryFn: ({ signal }) =>
      creatorFinancialApi.receivableSnapshots.list(params, signal),
    staleTime: 2 * 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar recebíveis",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    snapshots: query.data?.data ?? [],
    page: query.data?.page ?? 1,
    limit: query.data?.limit ?? 50,
    totalItems: query.data?.totalItems ?? 0,
    totalPages: query.data?.totalPage ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useCreatorPayments(
  params?: GetCreatorPaymentsParams,
  options?: UseQueryOptions<PaginatedCreatorPaymentsResponse, Error>
) {
  const { toast } = useToast();
  const query = useQuery({
    queryKey: creatorFinancialKeys.payments(params),
    queryFn: ({ signal }) => creatorFinancialApi.payments.list(params, signal),
    staleTime: 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar pagamentos",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    payments: query.data?.data ?? [],
    page: query.data?.page ?? 1,
    limit: query.data?.limit ?? 10,
    totalItems: query.data?.totalItems ?? 0,
    totalPages: query.data?.totalPage ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    queryResult: query,
  };
}
